import { useEffect, useRef } from 'react';

const SCROLL_EDGE = 2;

const getScrollHint = (el) => {
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflow = scrollHeight - clientHeight > SCROLL_EDGE;

    if (!overflow) return 'none';

    const atTop = scrollTop <= SCROLL_EDGE;
    const atBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_EDGE;

    if (atTop) return 'down';
    if (atBottom) return 'up';
    return 'both';
};

/**
 * Sets data-scroll-hint on a scroll container: none | down | up | both.
 * Used for sidebar fade + scrollbar affordance on desktop browse rails.
 */
export function useScrollOverflowHint() {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        const sync = () => {
            const next = getScrollHint(el);
            if (el.dataset.scrollHint !== next) {
                el.dataset.scrollHint = next;
            }
        };

        sync();
        el.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync);

        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
        resizeObserver?.observe(el);

        const mutationObserver = typeof MutationObserver !== 'undefined'
            ? new MutationObserver(sync)
            : null;
        mutationObserver?.observe(el, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        return () => {
            el.removeEventListener('scroll', sync);
            window.removeEventListener('resize', sync);
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
        };
    }, []);

    return ref;
}

export default useScrollOverflowHint;
